/**
 * EDO document detail (`/processes/[id]`) — те же блоки, что и дровер (макет «Карточка процесса»,
 * 16.10.2026): шапка документа, итог под шапкой, вкладки «Маршрут · Детали · История» и футер
 * действий, закреплённый внизу экрана (раньше действия шли после истории и без прокрутки видны не
 * были). Согласующий видит документ и решает (согласовать / доработка / отказ / напомнить), не
 * уходя со страницы.
 *
 * VM собирается из живой карточки (`reqVM`) + моей открытой задачи (`useMyTasks`), поэтому кнопки
 * решений совпадают с тем, что показал бы дровер, открытый из «Ждут моего решения».
 */
import { useState } from 'react'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { Stack, Text, XStack, YStack } from 'tamagui'

import { useApp } from '../../../src/i18n'
import { useDocumentTitle } from '../../../src/lib/useDocumentTitle'
import { useResponsive } from '../../../src/lib/responsive'
import { useEdoRequest, useMyTasks } from '../../../src/features/edo/useEdo'
import { useMe } from '../../../src/features/users/useUsers'
import { isLocalTerminationDraft } from '../../../src/features/edo/termination-types'
import { cardRoute, docNo, reqVM, routeOf, withMyTask, type ProcVM } from '../../../src/features/edo/processVM'
import { EdoStatusBadge } from '../../../src/features/edo/components/EdoStatusBadge'
import { fmtDateTime } from '../../../src/features/edo/components/CardDetails'
import { CardSummary, CardTabContent, CardTabs, type CardTab } from '../../../src/features/edo/components/ProcessCard'
import { ProcessActions, ProcessWorkflow } from '../../../src/features/edo/components/ProcessActions'
import { ApiError } from '../../../src/api/client'
import { typeOf } from '../../../src/features/edo/registry'
import { tone } from '../../../src/theme/tones'
import { Card, Enter, Icon, Skeleton } from '../../../src/ui'
import { Page } from '../../../src/ui/Screen'

const COLUMN = 760

export default function ProcessDetailScreen() {
  const { t, l } = useApp()
  const { isPhone } = useResponsive()
  const router = useRouter()
  const { id } = useLocalSearchParams<{ id: string }>()
  const reqId = String(id ?? '')
  const [tab, setTab] = useState<CardTab>('route')

  const { data: detail, isLoading, isError, error } = useEdoRequest(reqId)
  const forbidden = error instanceof ApiError && error.status === 403
  const { data: myTasks } = useMyTasks()
  const { data: me } = useMe()
  // Rebuild the VM the drawer would receive: live card fields (reqVM) + my open task's
  // action flags (taskId/stage/canDecide, withMyTask) so decision buttons show for the right approver.
  const vm: ProcVM | null = detail
    ? { ...withMyTask(reqVM(detail), myTasks, detail.tasks), mine: !!me && detail.initiator?.id === me.id }
    : null
  useDocumentTitle(vm ? `№ ${docNo(vm)}` : l('proc.title'))
  const route = detail && !isLocalTerminationDraft(detail) ? routeOf(detail.tasks, cardRoute(detail)) : []

  const back = (
    <XStack
      onPress={() => router.back()}
      cursor="pointer"
      animation="quick"
      alignItems="center"
      gap={6}
      alignSelf="flex-start"
      paddingVertical={4}
      hoverStyle={{ opacity: 0.8 }}
    >
      <Icon name="arrow-left" size={16} color="$primary" />
      <Text fontSize={13} fontWeight="600" color="$primary">
        {t({ ru: 'К процессам', en: 'Back to processes' })}
      </Text>
    </XStack>
  )

  const type = vm ? typeOf(vm.type) : undefined
  const tn = type ? tone(type.tone) : undefined

  return (
    <YStack flex={1}>
      <Page maxWidth={COLUMN}>
        {back}

        {isLoading ? (
          <Card padding={20} gap={12}>
            <Skeleton height={22} width="50%" />
            <Skeleton height={120} />
          </Card>
        ) : isError || !detail || !vm || !type || !tn ? (
          <Card padding={40} alignItems="center">
            <Text fontSize={14} fontWeight="700" color="$color">
              {forbidden ? l('proc.forbidden') : t({ ru: 'Документ не найден', en: 'Document not found' })}
            </Text>
          </Card>
        ) : (
          <>
            {/* Шапка документа — на акцентной подложке, чтобы читалась первой; ссылка — в меню «⋯».
                Под заголовком — номер и дата создания; содержимое заявки (период отпуска и т.п.) — во
                «Деталях»: в шапке его принимали за даты документа (30.10.2026). */}
            <Enter index={0}>
              <Card padding={20} gap={12} backgroundColor="$primaryBg" borderColor="$primary">
                <XStack alignItems="center" gap={12} flexWrap={isPhone ? 'wrap' : 'nowrap'}>
                  <Stack width={46} height={46} flexShrink={0} borderRadius={7} backgroundColor={tn.soft} alignItems="center" justifyContent="center">
                    <Icon name={type.icon} size={22} color={tn.text} />
                  </Stack>
                  <YStack flex={1} minWidth={isPhone ? 180 : 0}>
                    <Text fontSize={17} fontWeight="800" color="$color" numberOfLines={1}>
                      {t(vm.title ?? type.name)}
                    </Text>
                    <Text fontSize={12} color="$muted" marginTop={2} numberOfLines={2}>
                      {`№ ${docNo(vm)} · ${l('card.created')} ${fmtDateTime(detail.createdAt)}`}
                    </Text>
                  </YStack>
                  <EdoStatusBadge status={detail.status} />
                </XStack>
              </Card>
            </Enter>

            <Enter index={1}>
              <YStack gap={12}>
                <CardSummary p={vm} detail={detail} route={route} />
                <ProcessWorkflow detail={detail} onDone={() => router.back()} />
                <CardTabs value={tab} onChange={setTab} detail={detail} />
              </YStack>
            </Enter>

            <Enter index={2}>
              <Card padding={20}>
                <CardTabContent tab={tab} p={vm} detail={detail} route={route} />
              </Card>
            </Enter>
          </>
        )}

        <Stack height={8} />
      </Page>

      {/* Действия закреплены внизу экрана — видны без прокрутки, лист формы открывается поверх */}
      {vm && detail ? <ProcessActions p={vm} detail={detail} onClose={() => router.back()} maxWidth={COLUMN - 48} /> : null}
    </YStack>
  )
}
